import React, { useState } from 'react'

/**
 * ActiveCallHeaderDock
 * Global minimizable top blue header dock bar for active calls.
 * Displays call state, duration timer, audio controls (mute, hold, end call),
 * and an expand button to restore the full InboundCallPopupDrawer.
 */
export default function ActiveCallHeaderDock({
  isActive = true,
  callDirection = 'INBOUND',
  callerName = 'Customer',
  callerNumber = '',
  durationText = '',
  callDurationSecs = 0,
  callerInfo = null,
  isMuted = false,
  isOnHold = false,
  onToggleMute = () => {},
  onToggleHold = () => {},
  onEndCall = null,
  onHangup = null,
  onExpandDrawer = null,
  onExpand = null,
}) {
  if (!isActive) return null

  // Normalize prop sources
  const effectiveDirection = (callerInfo?.direction || callDirection || 'INBOUND').toUpperCase()
  const effectiveCallerName = callerInfo?.customer_name || callerInfo?.caller_name || callerName || 'Customer'
  const effectiveCallerNumber = callerInfo?.from || callerInfo?.called_number || callerInfo?.caller_number || callerNumber || ''

  // Normalize handlers
  const handleEndCall = onEndCall || onHangup || (() => {})
  const handleExpand = onExpandDrawer || onExpand || (() => {})

  // Compute duration display
  const m = Math.floor((callDurationSecs || 0) / 60).toString().padStart(2, '0')
  const s = ((callDurationSecs || 0) % 60).toString().padStart(2, '0')
  const displayDuration = durationText && durationText !== '04:35' ? durationText : `${m}:${s}`

  return (
    <div className="w-full bg-[#0052CC] text-white px-6 py-3 shadow-md flex items-center justify-between transition-all duration-300 z-50">
      {/* Left: Phone icon & Call Details */}
      <div className="flex items-center gap-4">
        <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center animate-pulse">
          <span className="material-symbols-outlined text-white text-xl">call</span>
        </div>
        <div>
          <div className="text-[11px] font-bold tracking-wider text-blue-200 uppercase">
            ACTIVE CALL: {effectiveDirection}
          </div>
          <div className="text-base font-semibold font-display">
            {effectiveCallerName} {effectiveCallerNumber && <span className="text-sm font-normal text-blue-100">({effectiveCallerNumber})</span>}
          </div>
        </div>
      </div>

      {/* Middle: Tabular Live Duration Timer */}
      <div className="flex flex-col items-center">
        <span className="text-[10px] uppercase tracking-widest text-blue-200">Duration</span>
        <span className="text-xl font-bold font-mono tracking-wider">{displayDuration}</span>
      </div>

      {/* Right: Controls (Mute, Hold, End Call, Expand Drawer) */}
      <div className="flex items-center gap-3">
        {/* Mute toggle */}
        <button
          type="button"
          onClick={onToggleMute}
          title={isMuted ? 'Unmute' : 'Mute'}
          className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
            isMuted ? 'bg-amber-500 text-white' : 'bg-white/20 hover:bg-white/30 text-white'
          }`}
        >
          <span className="material-symbols-outlined text-xl">
            {isMuted ? 'mic_off' : 'mic'}
          </span>
        </button>

        {/* Hold toggle */}
        <button
          type="button"
          onClick={onToggleHold}
          title={isOnHold ? 'Unhold' : 'Hold'}
          className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
            isOnHold ? 'bg-amber-500 text-white' : 'bg-white/20 hover:bg-white/30 text-white'
          }`}
        >
          <span className="material-symbols-outlined text-xl">
            {isOnHold ? 'play_arrow' : 'pause'}
          </span>
        </button>

        {/* End Call Pill Button */}
        <button
          type="button"
          onClick={handleEndCall}
          className="px-5 py-2 rounded-full bg-red-600 hover:bg-red-700 text-white font-semibold text-sm flex items-center gap-2 shadow-sm transition-colors"
        >
          <span className="material-symbols-outlined text-lg">call_end</span>
          <span>End Call</span>
        </button>

        {/* Expand Drawer Button */}
        <button
          type="button"
          onClick={handleExpand}
          title="Expand Call Drawer"
          className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white ml-2 transition-colors"
        >
          <span className="material-symbols-outlined text-xl">open_in_full</span>
        </button>
      </div>
    </div>
  )
}
